import { rowsPayloadOf, rowsToCsvMatrix } from "./rowsFormat"
import type { WidgetPayload } from "./types"
import { isActivityFeedItem } from "./types"

const NEEDS_QUOTING = /[",\n\r]/
const FORMULA_TRIGGER = /^[=+\-@\t\r]/

export const csvEscape = (value: unknown): string => {
  const text = value == null ? "" : String(value)
  // Neutralize spreadsheet formula injection only for author text; numbers keep their leading '-'.
  const guarded =
    typeof value === "string" && FORMULA_TRIGGER.test(text) ? `'${text}` : text
  return NEEDS_QUOTING.test(guarded)
    ? `"${guarded.replace(/"/g, '""')}"`
    : guarded
}

const toCsv = (rows: unknown[][]): string =>
  rows.map((row) => row.map(csvEscape).join(",")).join("\r\n")

const filenameFromTitle = (title: string | undefined): string => {
  const slug = (title ?? "widget")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return `${slug || "widget"}.csv`
}

export interface WidgetCsv {
  filename: string
  content: string
}

interface WidgetCsvInput {
  title?: string
  payload: WidgetPayload | null | undefined
}

const rowsForPayload = (payload: WidgetPayload): unknown[][] | null => {
  const rows = rowsPayloadOf(payload)
  if (rows) {
    return rowsToCsvMatrix(rows)
  }

  if (payload.tile) {
    const tile = payload.tile
    return [
      ["metric", "value", "unit"],
      [tile.title ?? tile.id, tile.value, tile.unit ?? ""]
    ]
  }

  if (payload.snapshot) {
    const { rows = [], columns = [], cells } = payload.snapshot
    const byPosition = new Map(
      cells.map((cell) => [`${cell.row}.${cell.column}`, cell])
    )
    return [
      ["", ...columns.map((column) => column.title)],
      ...rows.map((row) => [
        row.title,
        ...columns.map((column) => {
          const cell = byPosition.get(`${row.key}.${column.key}`)
          if (!cell) return ""
          return cell.has_data === false ? 0 : cell.value
        })
      ])
    ]
  }

  if (payload.chart) {
    const chart = payload.chart
    const isDonut =
      chart.visualisation === "donut" || chart.visualisation === "pie"
    if (isDonut && chart.status) {
      const status = chart.status
      return [
        ["category", "count"],
        ["on_track", status.on_track],
        ["at_risk", status.at_risk],
        ["delayed", status.delayed],
        ["total", status.total]
      ]
    }
    if (chart.marketing) {
      const { rows, totals, period } = chart.marketing
      return [
        [
          "source",
          "period_start",
          "period_end",
          "new_leads",
          "engaged_leads",
          "allocated_budget",
          "cost_per_new_lead",
          "cost_per_engaged_lead"
        ],
        ...rows.map((row) => [
          row.label,
          period.start,
          period.end,
          row.new_leads,
          row.engaged_leads,
          row.budget,
          row.cost_per_new_lead ?? "",
          row.cost_per_engaged_lead ?? ""
        ]),
        [
          "Total",
          period.start,
          period.end,
          totals.new_leads,
          totals.engaged_leads,
          totals.budget,
          totals.cost_per_new_lead ?? "",
          totals.cost_per_engaged_lead ?? ""
        ]
      ]
    }
    if (chart.engagement) {
      const { groups, defaultGroup, totals } = chart.engagement
      const rows = groups?.[defaultGroup ?? "week"] ?? []
      const rate = (met: number, added: number) =>
        added > 0 ? Math.round((met / added) * 100) : ""
      return [
        ["period_start", "period_end", "added", "engaged", "engagement_rate"],
        ...rows.map((row) => [
          row.start,
          row.end,
          row.new,
          row.engaged,
          rate(row.cohort_engaged, row.new)
        ]),
        [
          "total",
          "",
          totals.new,
          totals.engaged,
          rate(totals.cohort_engaged ?? 0, totals.new)
        ]
      ]
    }
    if (chart.cashFlow) {
      return [
        ["date", "in", "out", "net"],
        ...chart.cashFlow.series.map((point) => [
          point.date,
          point.in,
          point.out,
          point.net
        ])
      ]
    }
    return null
  }

  if (payload.feed) {
    const items = payload.feed.items
    if (items.length > 0 && items.every(isActivityFeedItem)) {
      return [
        ["event", "title", "timestamp"],
        ...items.map((item) => [item.event, item.title, item.timestamp])
      ]
    }
    return [
      ["severity", "title", "rationale"],
      ...items.map((item) =>
        isActivityFeedItem(item)
          ? ["", item.title, ""]
          : [item.severity, item.title, item.rationale]
      )
    ]
  }

  if (payload.alert) {
    return [
      ["severity", "alert", "title", "count"],
      ...payload.alert.items.map((item) => [
        item.severity ?? "",
        item.alert ?? "",
        item.title,
        item.count ?? ""
      ])
    ]
  }

  return null
}

export const buildWidgetCsv = ({
  title,
  payload
}: WidgetCsvInput): WidgetCsv | null => {
  if (!payload) return null
  const rows = rowsForPayload(payload)
  if (!rows) return null
  return { filename: filenameFromTitle(title), content: toCsv(rows) }
}

export const triggerCsvDownload = (filename: string, content: string): void => {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function")
    return
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.style.display = "none"
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export const exportWidgetCsv = (input: WidgetCsvInput): boolean => {
  const csv = buildWidgetCsv(input)
  if (!csv) return false
  triggerCsvDownload(csv.filename, csv.content)
  return true
}
